import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { VideoPreview } from 'site-modules/editorial/components/best-cars/video-preview/video-preview';
import { getFullImageUrl } from 'client/utils/image-helpers';
import { VIDEO_CARD_THUMBNAIL_SIZE, VIDEO_CARD_THUMBNAIL_SIZE_WIRED } from 'site-modules/editorial/constants/best-cars';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { Link } from 'site-modules/shared/components/link/link';

const VIDEO_CREATIVE_ID = 'card-video';

export function Winners({ vehicleCardEntries, onToggleVideoModal = null, isMobile = false }) {
  return (
    <article className="highlights-and-winners pos-r container pb-3 px-0_75 px-md-0">
      {vehicleCardEntries.map(entry => {
        // video
        const videoEntry = entry.child('card-video');
        const videoId = videoEntry.metadata('youtube-videoid').value();
        const videoTitle = videoEntry.metadata('videoTitle').value();
        const videoTranscript = videoEntry.metadata('videoTranscript').value();
        const videoThumbnailUrl = videoEntry.metadata('thumbnailURL').value();
        const videoAspectRatio = videoEntry.metadata('aspectRatio').value();
        const transcriptScrollTopId = `scrollTop-transcript-${videoId}`;
        const linkHref =
          entry.child('card-buttons').metadata('button2-url-top-rated').value() ||
          entry.child('card-buttons').metadata('button2-url').value();

        return (
          <Fragment key={entry.metadata('anchorName').value()}>
            <section
              id={entry.metadata('anchorName').value()}
              className="p-md-0_5 rounded-12 bg-white mb-2 mx-auto"
              tabIndex={-1}
            >
              <div className="d-flex flex-column align-items-center p-2 p-md-3">
                <figure
                  className="px-0 pe-lg-0_25 mb-0 w-100"
                  style={{
                    aspectRatio: '300/169',
                  }}
                >
                  <VideoPreview
                    videoId={videoId}
                    videoTitle={videoTitle}
                    videoAspectRatio={videoAspectRatio}
                    videoThumbnailUrl={
                      videoThumbnailUrl
                        ? getFullImageUrl(
                            videoThumbnailUrl,
                            isMobile ? VIDEO_CARD_THUMBNAIL_SIZE : VIDEO_CARD_THUMBNAIL_SIZE_WIRED
                          )
                        : null
                    }
                    openVideoModal={onToggleVideoModal}
                    creativeId={VIDEO_CREATIVE_ID}
                    isStaticPos={isMobile}
                  />
                  {videoTranscript && (
                    <ContentCollapse
                      textClosed="See transcript"
                      textOpen="Close transcript"
                      justify="justify-content-start"
                      btnClasses="me-0_25 p-0"
                      btnContainerClasses="transcript-button"
                      btnTextClasses="fw-normal medium"
                      btnArrowClasses="size-10 text-primary-darker"
                      classes="transcript mt-0_5 px-0_5"
                      showBtnBelow
                      isEclipseFade={false}
                      resetParams={{
                        offset: SCROLL_OFFSET,
                        duration: SCROLL_DURATION,
                        to: transcriptScrollTopId,
                      }}
                    >
                      <ScrollElement id={transcriptScrollTopId} />
                      <div className="py-0_25 text-gray-darker">{videoTranscript}</div>
                    </ContentCollapse>
                  )}
                </figure>
                <header className="px-0 text-center">
                  <h2 className="text-blue-40 fw-bold text-uppercase mb-md-0_25">
                    <span className="size-16 hidden-md-down">{`Edmunds ${entry.metadata('card-title').value()}`}</span>
                    <span className="size-14 hidden-md-up py-0_25 px-1 bg-blue-100 rounded-12">{`Edmunds ${entry.metadata('card-title').value()}`}</span>
                  </h2>
                  <h3 className="text-cool-gray-10 fw-bold mb-1">
                    <span className="size-30 hidden-md-down">
                      {entry.child('vehicle-title').metadata('vehicle-title').value()}
                    </span>
                    <span className="size-24 hidden-md-up">
                      {entry.child('vehicle-title').metadata('vehicle-title').value()}
                    </span>
                  </h3>
                  <h4 className="text-cool-gray-10 fw-normal fst-italic size-16">
                    {entry.child('vehicle-content').metadata('copyText').value()}
                  </h4>
                </header>
                <Link
                  className="btn btn-outline-blue-50 d-block px-1_5 py-1"
                  to={linkHref}
                  target={
                    entry.child('card-buttons').metadata('button2-url-blank').value() === 'true' ? '_blank' : null
                  }
                  rel={
                    entry.child('card-buttons').metadata('button2-url-nofollow').value() === 'true' ? 'nofollow' : null
                  }
                  data-tracking-id="view_content"
                >
                  <span className="size-16 mb-0">See Reviews and Pricing</span>
                </Link>
              </div>
            </section>
            <div aria-hidden className="hidden-md-down" style={{ height: '32px' }} />
          </Fragment>
        );
      })}
    </article>
  );
}

Winners.propTypes = {
  vehicleCardEntries: PropTypes.array.isRequired,
  onToggleVideoModal: PropTypes.func,
  isMobile: PropTypes.bool,
};
